import { type CSSProperties, useEffect, useRef } from 'react';
import type { VoiceBands } from '../lib/pcm-player';

const BUFFER_CUSHION_S = 1.2;
const MAX_BUFFER_WAIT_MS = 3000;

type ChuckExperienceProps = {
  caption: string;
  captionIsUser: boolean;
  speaking: boolean;
  voiceBands: VoiceBands;
  videoSrc?: string | null;
  onVideoPlaying?: () => void;
  onVideoEnded?: () => void;
  onVideoError?: () => void;
};

type VoiceLampStyle = CSSProperties & { '--lamp-level': number };

export function ChuckExperience({
  caption,
  captionIsUser,
  speaking,
  voiceBands,
  videoSrc = null,
  onVideoPlaying,
  onVideoEnded,
  onVideoError,
}: ChuckExperienceProps) {
  const video = useRef<HTMLVideoElement>(null);
  const onVideoErrorRef = useRef(onVideoError);
  onVideoErrorRef.current = onVideoError;

  useEffect(() => {
    const element = video.current;
    if (!videoSrc || !element) return;
    let poll = 0;
    const bufferedAhead = () => (element.buffered.length
      ? element.buffered.end(element.buffered.length - 1) - element.currentTime
      : 0);
    const playWhenBuffered = () => {
      window.clearInterval(poll);
      let deadline = Number.POSITIVE_INFINITY;
      poll = window.setInterval(() => {
        const ahead = bufferedAhead();
        if (ahead > 0 && deadline === Number.POSITIVE_INFINITY) {
          deadline = performance.now() + MAX_BUFFER_WAIT_MS;
        }
        if (ahead < BUFFER_CUSHION_S && performance.now() < deadline) return;
        window.clearInterval(poll);
        element.play().catch((error: unknown) => {
          if ((error as Error)?.name !== 'AbortError') onVideoErrorRef.current?.();
        });
      }, 100);
    };
    const onWaiting = () => {
      element.pause();
      playWhenBuffered();
    };
    element.addEventListener('waiting', onWaiting);
    playWhenBuffered();
    return () => {
      window.clearInterval(poll);
      element.removeEventListener('waiting', onWaiting);
    };
  }, [videoSrc]);

  return (
    <div className={`chuck-experience ${speaking ? 'is-speaking' : ''}`}>
      <div className="chuck-stage">
        <img
          className="chuck-gallery-art"
          src="/assets/chuck/conductor-of-conversations-v1.png"
          alt="Chuck seated in a fantastical railway communications carriage, surrounded by painted scenes of routing, omnichannel contact, conversational AI, and analytics"
        />

        {videoSrc && (
          <video
            ref={video}
            className="chuck-portrait-video"
            src={videoSrc}
            playsInline
            aria-hidden="true"
            onPlaying={onVideoPlaying}
            onEnded={onVideoEnded}
            onError={onVideoError}
          />
        )}

        <div className="chuck-voice-lamps" aria-hidden="true">
          {voiceBands.map((level, index) => (
            <i
              key={index}
              style={{ '--lamp-level': speaking ? Math.max(.04, level) : .04 } as VoiceLampStyle}
            />
          ))}
        </div>

        <div className="chuck-nameplate" aria-label="Chuck, Conductor of Conversations">
          <strong>Chuck</strong>
          <span>Conductor of Conversations</span>
        </div>
      </div>

      <div
        className={`chuck-caption ${caption ? 'is-visible' : ''} ${captionIsUser ? 'is-user' : ''}`}
        aria-live="polite"
      >
        <span>{captionIsUser ? 'You' : 'Chuck'}</span>
        <p>{caption}</p>
      </div>
    </div>
  );
}
